import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Select, MenuItem, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Skeleton, Chip, Badge, useMediaQuery, useTheme as useMuiTheme
} from '@mui/material';
import {
  TableRows, Add, AttachMoney, CurrencyRupee,
  ArrowBack, Search, Refresh, DeleteOutline, Edit,
  WbSunny, NightsStay, Close, CloudOff, CloudDone,
  Sync, WifiOff, Print
} from '@mui/icons-material';
import Layout from '../components/Layout';
import api from '../api/authApi';
import { useApp } from '../context/AppContext';
import SmsSyncBanner from '../components/SmsSyncBanner';

const today = () => {
  const d = new Date();
  return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
};
const toISO = (dd) => {
  if (!dd) return '';
  if (dd.includes('-')) return dd;
  const [m, d, y] = dd.split('/');
  return `${y}-${m}-${d}`;
};
const fmt2 = (v) => parseFloat(v || 0).toFixed(2);
const fmt3 = (v) => parseFloat(v || 0).toFixed(3);

const PAGE_SIZES = [10, 20, 50, 100, 'All'];

// ─────────────────────────────────────────────────────
//  OFFLINE QUEUE — IndexedDB
// ─────────────────────────────────────────────────────
const DB_NAME = 'DairyProOffline';
const DB_VERSION = 3;
const STORE = 'collections_queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE))
        db.createObjectStore(STORE, { keyPath: 'localId', autoIncrement: true });
      if (!db.objectStoreNames.contains('farmers'))
        db.createObjectStore('farmers', { keyPath: 'farmerId' });
      if (!db.objectStoreNames.contains('rate_charts'))
        db.createObjectStore('rate_charts', { keyPath: 'milkTypeId' });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

async function getFarmerOffline(code) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction('farmers', 'readonly').objectStore('farmers').getAll();
      req.onsuccess = () => {
        const match = req.result.find(f =>
          String(f.farmerCode).trim().toLowerCase() === String(code).trim().toLowerCase()
        );
        resolve(match || null);
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function getRateOffline(milkTypeId, fat, snf) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction('rate_charts', 'readonly').objectStore('rate_charts').get(milkTypeId);
      req.onsuccess = () => {
        const chart = req.result;
        if (!chart) { resolve(null); return; }
        const grid = chart.gridJson;
        if (grid && grid.rows) {
          const fatKeys = Object.keys(grid.rows);
          const fatKey = fatKeys.find(f => Math.abs(parseFloat(f) - fat) < 0.051);
          if (fatKey) {
            const snfKey = (grid.colHeaders || []).find(s => Math.abs(parseFloat(s) - snf) < 0.051);
            if (snfKey) {
              const rate = grid.rows[fatKey][snfKey];
              if (rate != null) { resolve(parseFloat(rate)); return; }
            }
          }
        }
        resolve(chart.startingAmount > 0 ? chart.startingAmount : null);
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function getReceiptSettings() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction('settings_cache', 'readonly').objectStore('settings_cache').get('receipt_settings');
      req.onsuccess = () => { resolve(req.result?.value || {}); db.close(); };
      req.onerror  = () => { resolve({}); };
    });
  } catch { return {}; }
}
async function saveReceiptSettings(data) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('settings_cache', 'readwrite');
      tx.objectStore('settings_cache').put({ key: 'receipt_settings', value: data });
      tx.oncomplete = () => { db.close(); resolve(true); };
    });
  } catch { return false; }
}

function printReceipt(row, centerName, centerAddress, onAfterPrint) {
  const shiftLabel = row.shift === 1 ? 'Morning' : 'Evening';
  const milkLabel  = row.milkTypeId === 2 ? 'Buffalo' : 'Cow';
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
  const dateStr = row.collectionDate
    ? new Date(row.collectionDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
    : now.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });

  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<style>
  @page { size: 48mm auto; margin: 0; }
  * { box-sizing:border-box; margin:0; padding:0; font-family:'Courier New',monospace; }
  body { width:48mm; font-size:10px; color:#000; padding:2mm; }
  .center { text-align:center; }
  .bold { font-weight:bold; }
  .lg { font-size:13px; }
  .xl { font-size:16px; font-weight:bold; }
  .divider { border-top:1px dashed #000; margin:2mm 0; }
  .row { display:flex; justify-content:space-between; margin:1mm 0; }
  .row .label { color:#444; }
  .row .value { font-weight:bold; }
  .amount-box { border:1px solid #000; text-align:center; padding:2mm; margin:2mm 0; }
  .footer { text-align:center; font-size:9px; color:#666; margin-top:2mm; }
</style>
</head><body>
  <div class="center bold lg">${centerName || 'Dairy Center'}</div>
  ${centerAddress ? `<div class="center" style="font-size:9px;">${centerAddress.replace(/\n/g,'<br/>')}</div>` : ''}
  <div class="center" style="font-size:9px;margin-bottom:1mm;">Collection Receipt</div>
  <div class="divider"></div>
  <div class="row"><span class="label">Date</span><span class="value">${dateStr}</span></div>
  <div class="row"><span class="label">Time</span><span class="value">${timeStr}</span></div>
  <div class="row"><span class="label">Shift</span><span class="value">${shiftLabel}</span></div>
  <div class="divider"></div>
  <div class="row"><span class="label">Code</span><span class="value">${row.code || row.farmerCode || '-'}</span></div>
  <div class="row"><span class="label">Farmer</span><span class="value">${row.name || row.farmerName || '-'}</span></div>
  <div class="row"><span class="label">Milk</span><span class="value">${milkLabel}</span></div>
  <div class="divider"></div>
  <div class="row"><span class="label">Qty (L)</span><span class="value">${parseFloat(row.quantity).toFixed(3)}</span></div>
  <div class="row"><span class="label">FAT</span><span class="value">${parseFloat(row.fat).toFixed(1)}</span></div>
  <div class="row"><span class="label">SNF</span><span class="value">${parseFloat(row.snf).toFixed(1)}</span></div>
  <div class="row"><span class="label">Rate/L</span><span class="value">&#8377;${parseFloat(row.rate).toFixed(2)}</span></div>
  <div class="divider"></div>
  <div class="amount-box">
    <div style="font-size:9px;">TOTAL AMOUNT</div>
    <div class="xl">&#8377; ${parseFloat(row.amount).toFixed(2)}</div>
  </div>
  <div class="footer">Thank you! — Powered by DairyPro</div>
</body></html>`;

  // ✅ Print silently via hidden iframe — no preview, no new tab
  const old = document.getElementById('dp-print-frame');
  if (old) old.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'dp-print-frame';
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:48mm;height:1px;border:none;visibility:hidden;';
  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      // ✅ After print dialog closes (or auto-prints), callback to restore focus
      if (onAfterPrint) onAfterPrint();
    } catch(e) {
      console.warn('Print failed:', e);
    }
    setTimeout(() => { iframe.remove(); if (onAfterPrint) onAfterPrint(); }, 500);
  };

  iframe.srcdoc = html;
}

async function queueOffline(payload) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readwrite').objectStore(STORE).add({
      ...payload, status: 'pending', createdAt: new Date().toISOString(),
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}
async function getAllQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}
async function updateQueueEntry(localId, updates) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    const g = store.get(localId);
    g.onsuccess = () => {
      const p = store.put({ ...g.result, ...updates });
      p.onsuccess = () => resolve();
      p.onerror   = () => reject(p.error);
    };
  });
}
async function clearSyncedQueue() {
  const db = await openDB();
  return new Promise((resolve) => {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      req.result.filter(r => r.status === 'synced').forEach(r => store.delete(r.localId));
      resolve();
    };
  });
}
async function syncQueue() {
  const all = await getAllQueue();
  const pending = all.filter(e => e.status === 'pending');
  let synced = 0, failed = 0;
  for (const entry of pending) {
    try {
      const { localId, status, createdAt, syncedAt, error: _e, ...payload } = entry;
      const res = await api.post('/collection', payload);
      if (res.data.success) {
        await updateQueueEntry(localId, { status: 'synced', syncedAt: new Date().toISOString() });
        synced++;
      } else {
        await updateQueueEntry(localId, { status: 'failed', error: res.data.message });
        failed++;
      }
    } catch {
      await updateQueueEntry(entry.localId, { status: 'failed', error: 'Network error' });
      failed++;
    }
  }
  await clearSyncedQueue();
  return { synced, failed };
}

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const checkReal = useCallback(async () => {
    if (!navigator.onLine) { setIsOnline(false); return; }
    try { await api.get('/health', { timeout: 3000 }); setIsOnline(true); }
    catch { setIsOnline(false); }
  }, []);
  useEffect(() => {
    checkReal();
    const on  = () => checkReal();
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    const timer = setInterval(checkReal, 15000);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); clearInterval(timer); };
  }, [checkReal]);
  return isOnline;
}

function StatCard({ label, value, color }) {
  return (
    <Box sx={{ px:2, py:1.2, borderRadius:2, background:'#f8fffe', border:'1px solid #d1fae5', minWidth:110, textAlign:'center' }}>
      <Typography variant="caption" sx={{ color:'#888', display:'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight:800, color }}>{value}</Typography>
    </Box>
  );
}

function PaginationBar({ page, pageSize, totalCount, onPage, onPageSize }) {
  const isAll = pageSize === 'All';
  const totalPages = isAll ? 1 : (Math.ceil(totalCount / pageSize) || 1);
  const from = totalCount === 0 ? 0 : isAll ? 1 : (page - 1) * pageSize + 1;
  const to   = isAll ? totalCount : Math.min(page * pageSize, totalCount);
  return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1, borderTop:'1px solid #f0f0f0', flexWrap:'wrap', gap:1 }}>
      <Typography variant="caption" sx={{ color:'#777' }}>Showing {from} to {to} of {totalCount} entries</Typography>
      <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
        <IconButton size="small" onClick={() => onPage(1)}          disabled={isAll || page<=1}><Typography sx={{ fontSize:13, fontWeight:700 }}>«</Typography></IconButton>
        <IconButton size="small" onClick={() => onPage(page-1)}     disabled={isAll || page<=1}><Typography sx={{ fontSize:13, fontWeight:700 }}>‹</Typography></IconButton>
        <IconButton size="small" onClick={() => onPage(page+1)}     disabled={isAll || page>=totalPages}><Typography sx={{ fontSize:13, fontWeight:700 }}>›</Typography></IconButton>
        <IconButton size="small" onClick={() => onPage(totalPages)} disabled={isAll || page>=totalPages}><Typography sx={{ fontSize:13, fontWeight:700 }}>»</Typography></IconButton>
        <Select value={pageSize} onChange={(e) => onPageSize(e.target.value)} size="small" sx={{ ml:1, fontSize:'0.78rem', height:30, minWidth:65 }}>
          {PAGE_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
      </Box>
    </Box>
  );
}

function ShiftBadge({ shift }) {
  const isMorning = shift === 1 || shift === '1';
  return (
    <Chip
      icon={isMorning ? <WbSunny sx={{ fontSize:'14px !important', color:'#f59e0b !important' }}/> : <NightsStay sx={{ fontSize:'14px !important', color:'#6366f1 !important' }}/>}
      label={isMorning ? 'Morning' : 'Evening'} size="small"
      sx={{ fontSize:'0.7rem', fontWeight:700, height:22, background:isMorning?'#fffbeb':'#eef2ff', color:isMorning?'#d97706':'#4f46e5', border:isMorning?'1px solid #fde68a':'1px solid #c7d2fe', '& .MuiChip-icon':{ ml:'4px' } }}
    />
  );
}

function MilkBadge({ typeId }) {
  const isCow = typeId === 1 || typeId === '1';
  return <Typography variant="caption" sx={{ fontWeight:700, fontSize:'0.7rem', color:isCow?'#059669':'#7c3aed' }}>{isCow ? 'Cow' : 'Buffalo'}</Typography>;
}

function StatusChip({ status }) {
  const map = {
    pending: { label:'Pending', bg:'#fff7ed', color:'#f97316', border:'#f9731633' },
    synced:  { label:'Synced',  bg:'#f0fdf4', color:'#16a34a', border:'#1a7a4a33' },
    failed:  { label:'Failed',  bg:'#fff5f5', color:'#e53e3e', border:'#e53e3e33' },
  };
  const s = map[status] || map.pending;
  return <Chip label={s.label} size="small" sx={{ fontWeight:700, fontSize:'0.7rem', background:s.bg, color:s.color, border:`1px solid ${s.border}` }}/>;
}

export default function Collections() {
  const [view, setView] = useState('list');
  return (
    <>
      {view === 'list'       && <ListView         setView={setView} />}
      {view === 'add'        && <AddView          setView={setView} />}
      {view === 'adjustment' && <AdjustmentView   setView={setView} />}
      {view === 'offline'    && <OfflineQueueView  setView={setView} />}
    </>
  );
}

// ═════════════════════════════════════════════════════
//  LIST VIEW
// ═════════════════════════════════════════════════════
function ListView({ setView }) {
  const { theme, t, user } = useApp();
  const centerName = user?.centerName || 'Dairy Center';
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [filterDate,   setFilterDate]   = useState(new Date().toISOString().split('T')[0]);
  const [filterShift,  setFilterShift]  = useState('');
  const [filterMilk,   setFilterMilk]   = useState('');
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(20);
  const [data,         setData]         = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [stats,        setStats]        = useState(null);
  const [rateDialog,     setRateDialog]     = useState(false);
  const [updating,       setUpdating]       = useState(false);
  const [receiptDialog,  setReceiptDialog]  = useState(false);
  const [receiptAddress, setReceiptAddress] = useState('');
  const [savingReceipt,  setSavingReceipt]  = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing,      setSyncing]      = useState(false);
  const isOnline = useOnlineStatus();

  const btnSx = { background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, borderRadius:2, fontWeight:700, fontSize:'0.8rem', '&:hover':{ background:`linear-gradient(135deg,${theme.hover},${theme.primary})` } };

  const refreshPending = useCallback(async () => {
    try { const all = await getAllQueue(); setPendingCount(all.filter(e => e.status === 'pending').length); } catch {}
  }, []);

  useEffect(() => {
    refreshPending();
    getReceiptSettings().then(s => { if (s.address) setReceiptAddress(s.address); });
  }, [refreshPending]);
  useEffect(() => {
    if (isOnline) { refreshPending(); cacheRateCharts(); }
  }, [isOnline, refreshPending]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { synced, failed } = await syncQueue();
      await refreshPending(); fetchData();
      setSuccess(`Sync done: ${synced} saved${failed > 0 ? `, ${failed} failed` : ''}. ✅`);
      setTimeout(() => setSuccess(''), 4000);
    } catch { setError('Sync failed.'); }
    finally { setSyncing(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: pageSize === 'All' ? '99999' : String(pageSize) });
      if (filterDate)  params.append('date', filterDate);
      if (filterShift) params.append('shift',      filterShift);
      if (filterMilk)  params.append('milkTypeId', filterMilk);
      if (search)      params.append('search',     search);
      const res = await api.get(`/collection?${params}`);
      if (res.data.success) { setData(res.data.data?.data || []); setTotalCount(res.data.data?.totalCount || 0); }
    } catch {}
    finally { setLoading(false); }
  }, [page, pageSize, filterDate, filterShift, filterMilk, search]);

  const fetchStats = useCallback(async () => {
    if (!filterDate) return;
    try { const res = await api.get(`/collection/stats?date=${filterDate}`); if (res.data.success) setStats(res.data.data); } catch {}
  }, [filterDate]);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      const res = await api.delete(`/collection/${id}`);
      if (res.data.success) { setSuccess('Deleted!'); fetchData(); fetchStats(); }
      else setError(res.data.message);
    } catch { setError('Delete failed.'); }
  };

  const cacheRateCharts = async () => {
    try {
      const [cow, buf] = await Promise.all([api.get('/ratechart/1'), api.get('/ratechart/2')]);
      const db = await openDB();
      const tx = db.transaction('rate_charts', 'readwrite');
      const store = tx.objectStore('rate_charts');
      for (const res of [cow, buf]) {
        const d = res.data?.data;
        if (!d) continue;
        const grid = typeof d.gridJson === 'string' ? JSON.parse(d.gridJson) : d.gridJson;
        store.put({ milkTypeId: d.milkTypeId, chartType: d.chartType, startingAmount: d.startingAmount, gridJson: grid, savedAt: new Date().toISOString() });
      }
      tx.oncomplete = () => db.close();
    } catch (e) { console.warn('Rate chart cache failed:', e); }
  };

  const handleUpdateRates = async () => {
    setUpdating(true);
    try {
      const res = await api.post('/collection/update-rates', { date: filterDate });
      if (res.data.success) { setSuccess(res.data.message || 'Rates updated!'); fetchData(); }
      else setError(res.data.message);
    } catch { setError('Rate update failed.'); }
    finally { setUpdating(false); setRateDialog(false); }
  };

  const thSx = { background:'#f8fffe', fontWeight:800, color:'#444', fontSize:'0.75rem', padding:'10px 12px', borderBottom:'2px solid #e0f2e9', whiteSpace:'nowrap', textAlign:'left' };
  const tdSx = { fontSize:'0.78rem', padding:'8px 12px', borderBottom:'1px solid #f5f5f5', whiteSpace:'nowrap' };

  return (
    <Layout title="Collections">
      <SmsSyncBanner />
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb:2, borderRadius:2 }} onClose={() => setError('')}>{error}</Alert>}

      {(pendingCount > 0 || !isOnline) && (
        <Box sx={{ mb:1.5, px:2, py:1, display:'flex', alignItems:'center', gap:1.5, background:isOnline?theme.light:'#fff7ed', border:`1px solid ${isOnline?'#1a7a4a33':'#f9731633'}`, borderRadius:2 }}>
          {isOnline ? <CloudDone sx={{ fontSize:18, color:theme.primary }}/> : <WifiOff sx={{ fontSize:18, color:'#f97316' }}/>}
          <Typography variant="caption" sx={{ fontWeight:700, color:isOnline?theme.primary:'#f97316', flexGrow:1 }}>
            {isOnline ? `${pendingCount} offline entries ready to sync` : `Offline — ${pendingCount} entries queued locally`}
          </Typography>
          {isOnline && pendingCount > 0 && (
            <Button size="small" onClick={handleSync} disabled={syncing}
              startIcon={syncing ? <CircularProgress size={12} color="inherit"/> : <Sync sx={{ fontSize:14 }}/>}
              sx={{ fontWeight:700, fontSize:'0.72rem', color:theme.primary, border:'1px solid #1a7a4a44', borderRadius:1.5, py:0.3, px:1 }}>
              {syncing ? t('syncing') : t('syncNow')}
            </Button>
          )}
        </Box>
      )}

      <Card sx={{ borderRadius:3, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #f0f0f0', flexWrap:'wrap', gap:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <TableRows sx={{ color:theme.primary, fontSize:22 }}/>
            <Typography variant="h6" sx={{ fontWeight:800 }}>Collections</Typography>
          </Box>
          <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
            <Button size="small" startIcon={<Add/>} onClick={() => setView('add')} variant="outlined"
              sx={{ borderRadius:2, borderColor:theme.primary, color:theme.primary, fontWeight:700, '&:hover':{ background:theme.light } }}>
              Add Collection
            </Button>
            <Button size="small" startIcon={<AttachMoney sx={{ fontSize:16 }}/>} onClick={() => setView('adjustment')} variant="outlined"
              sx={{ borderRadius:2, borderColor:theme.primary, color:theme.primary, fontWeight:700, '&:hover':{ background:theme.light } }}>
              Collection Adjustment
            </Button>
            <Button size="small" startIcon={<CurrencyRupee sx={{ fontSize:16 }}/>} onClick={() => setRateDialog(true)} variant="outlined"
              sx={{ borderRadius:2, borderColor:theme.primary, color:theme.primary, fontWeight:700, '&:hover':{ background:theme.light } }}>
              Update Rates
            </Button>
            <Button size="small" startIcon={<Print sx={{ fontSize:16 }}/>} onClick={() => setReceiptDialog(true)} variant="outlined"
              sx={{ borderRadius:2, borderColor:theme.primary, color:theme.primary, fontWeight:700, '&:hover':{ background:theme.light } }}>
              Receipt
            </Button>
            <Badge badgeContent={pendingCount} color="warning" max={99}>
              <Button size="small" startIcon={<CloudOff sx={{ fontSize:16 }}/>} onClick={() => setView('offline')} variant="outlined"
                sx={{ borderRadius:2, borderColor:pendingCount>0?'#f97316':'#aaa', color:pendingCount>0?'#f97316':'#aaa', fontWeight:700, '&:hover':{ background:'#fff7ed' } }}>
                Offline Queue
              </Button>
            </Badge>
          </Box>
        </Box>

        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', gap:2, flexWrap:'wrap', borderBottom:'1px solid #f5f5f5', background:'#fafffe' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Date :</Typography>
            <TextField value={filterDate} onChange={(e) => setFilterDate(e.target.value)} type="date" size="small" sx={{ width:155, '& input':{ fontSize:'0.82rem', py:0.8 } }}/>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Shift :</Typography>
            <Select value={filterShift} onChange={(e) => setFilterShift(e.target.value)} size="small" displayEmpty sx={{ minWidth:110, fontSize:'0.82rem', '& .MuiSelect-select':{ py:0.8 } }}>
              <MenuItem value="">All</MenuItem><MenuItem value="1">{t('morning')}</MenuItem><MenuItem value="2">{t('evening')}</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Milk :</Typography>
            <Select value={filterMilk} onChange={(e) => setFilterMilk(e.target.value)} size="small" displayEmpty sx={{ minWidth:120, fontSize:'0.82rem', '& .MuiSelect-select':{ py:0.8 } }}>
              <MenuItem value="">All</MenuItem><MenuItem value="1">{t('cow')}</MenuItem><MenuItem value="2">{t('buffalo')}</MenuItem>
            </Select>
          </Box>
          <Button variant="contained" size="small" onClick={() => { setPage(1); fetchData(); fetchStats(); }} startIcon={<Search sx={{ fontSize:16 }}/>} sx={btnSx}>{t('search')}</Button>
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => { fetchData(); fetchStats(); }} sx={{ border:'1px solid #e0e0e0', borderRadius:1.5, color:theme.primary }}><Refresh fontSize="small"/></IconButton></Tooltip>
        </Box>

        {stats && stats.totalEntries > 0 && (
          <Box sx={{ px:2.5, py:1, display:'flex', gap:1.5, flexWrap:'wrap', borderBottom:'1px solid #f5f5f5', background:'#f8fffe' }}>
            <StatCard label="Entries"   value={stats.totalEntries}/>
            <StatCard label="Total Qty" value={`${fmt3(stats.totalQuantity)} L`}/>
            <StatCard label="Total Amt" value={`₹${fmt2(stats.totalAmount)}`}/>
            <StatCard label="Avg FAT"   value={fmt2(stats.avgFAT)}/>
            <StatCard label="Avg SNF"   value={fmt2(stats.avgSNF)}/>
            <StatCard label="Avg Rate"  value={`₹${fmt2(stats.avgRate)}`}/>
            <StatCard label="Morning"   value={stats.morningCount} color="#d97706"/>
            <StatCard label="Evening"   value={stats.eveningCount} color="#4f46e5"/>
          </Box>
        )}

        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', px:2, py:0.8, borderBottom:'1px solid #f0f0f0', background:'#fafffe' }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:'#777' }}>Collections</Typography>
          <TextField size="small" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} onKeyDown={(e) => e.key==='Enter' && fetchData()}
            InputProps={{ startAdornment: <Search sx={{ fontSize:16, color:'#aaa', mr:0.5 }}/> }} sx={{ width:180, '& input':{ fontSize:'0.78rem', py:0.7 } }}/>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{[t('code'),t('name'),t('shift'),'Milk','Quantity','FAT','SNF','CLR',t('rate'),t('amount'),t('action')].map(col => (
              <th key={col} style={{ ...thSx, textAlign:['Quantity','FAT','SNF','CLR',t('rate'),t('amount')].includes(col)?'right':'left' }}>{col}</th>
            ))}</tr></thead>
            <tbody>
              {loading ? Array.from({ length:6 }).map((_,i) => <tr key={i}>{Array.from({ length:11 }).map((__,j) => <td key={j} style={tdSx}><Skeleton height={18}/></td>)}</tr>)
              : data.length === 0 ? <tr><td colSpan={11} style={{ textAlign:'center' }}><Typography variant="body1" sx={{ color:'#bbb', py:4 }}>{t('noRecords')}</Typography></td></tr>
              : data.map((row, idx) => (
                <tr key={row.collectionId} style={{ background:idx%2===0?'#fff':'#fafffe' }}>
                  <td style={{ ...tdSx, fontWeight:700, color:theme.primary }}>{row.code}</td>
                  <td style={tdSx}>{row.name}</td>
                  <td style={tdSx}><ShiftBadge shift={row.shift}/></td>
                  <td style={tdSx}><MilkBadge typeId={row.milkTypeId}/></td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt3(row.quantity)}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt2(row.fat)}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt2(row.snf)}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt2(row.clr)}</td>
                  <td style={{ ...tdSx, textAlign:'right', color:theme.primary, fontWeight:700 }}>{fmt2(row.rate)}</td>
                  <td style={{ ...tdSx, textAlign:'right', fontWeight:700 }}>₹{fmt2(row.amount)}</td>
                  <td style={tdSx}>
                    <Tooltip title="Print"><IconButton size="small" onClick={() => printReceipt(row, centerName, receiptAddress)} sx={{ color:'#1a7a4a', p:0.3 }}><Print sx={{ fontSize:16 }}/></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(row.collectionId)} sx={{ color:'#e53e3e', p:0.3 }}><DeleteOutline sx={{ fontSize:16 }}/></IconButton></Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && data.length > 0 && (() => {
              const totalQty = data.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
              const totalAmt = data.reduce((s, r) => s + (parseFloat(r.amount)   || 0), 0);
              return (
                <tfoot>
                  <tr style={{ background:'#f0fdf4', borderTop:'2px solid #a7f3d0' }}>
                    <td colSpan={4} style={{ padding:'9px 12px', fontWeight:800, fontSize:'0.78rem', color:'#1a7a4a' }}>
                      {pageSize === 'All' ? 'Grand Total' : 'Page Total'}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:800, fontSize:'0.78rem', color:'#1a7a4a' }}>{fmt3(totalQty)} L</td>
                    <td colSpan={4} style={{ padding:'9px 12px' }}></td>
                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:800, fontSize:'0.78rem', color:'#1a7a4a' }}>₹{fmt2(totalAmt)}</td>
                    <td style={{ padding:'9px 12px' }}></td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </Box>
        <PaginationBar page={page} pageSize={pageSize} totalCount={totalCount} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }}/>
      </Card>

      <Dialog open={rateDialog} onClose={() => setRateDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', py:1.5, background:theme.light, fontWeight:700, fontSize:'0.95rem', borderBottom:'1px solid #d1fae5' }}>
          Update Collection Rate <IconButton size="small" onClick={() => setRateDialog(false)}><Close fontSize="small"/></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px:3, py:3 }}>
          <Typography variant="body2" sx={{ color:'#444', lineHeight:1.7 }}>Update rates for all collections on <strong>{filterDate}</strong> using current rate card?</Typography>
        </DialogContent>
        <DialogActions sx={{ px:3, py:2, gap:1 }}>
          <Button onClick={() => setRateDialog(false)} variant="outlined" sx={{ borderRadius:2, color:'#888', borderColor:'#ddd' }}>No</Button>
          <Button onClick={handleUpdateRates} variant="contained" disabled={updating} startIcon={updating ? <CircularProgress size={14} color="inherit"/> : null}
            sx={{ background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, borderRadius:2, fontWeight:700 }}>{updating ? 'Updating...' : 'Yes, Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiptDialog} onClose={() => setReceiptDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', py:1.5, background:theme.light, fontWeight:700, fontSize:'0.95rem', borderBottom:'1px solid #d1fae5' }}>
          🖨 Receipt Settings <IconButton size="small" onClick={() => setReceiptDialog(false)}><Close fontSize="small"/></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px:3, py:3 }}>
          <Typography variant="body2" sx={{ color:'#666', mb:2 }}>This text will appear on every printed receipt below the center name.</Typography>
          <TextField label="Center Address" multiline rows={3} fullWidth value={receiptAddress}
            onChange={(e) => setReceiptAddress(e.target.value)}
            placeholder={"e.g.\nMain Road, Pune\nMobile: 9876543210"}
            size="small" sx={{ '& .MuiOutlinedInput-root':{ borderRadius:2 } }}/>
        </DialogContent>
        <DialogActions sx={{ px:3, py:2, gap:1 }}>
          <Button onClick={() => setReceiptDialog(false)} variant="outlined" sx={{ borderRadius:2, color:'#888', borderColor:'#ddd' }}>Cancel</Button>
          <Button onClick={async () => { setSavingReceipt(true); await saveReceiptSettings({ address: receiptAddress }); setSavingReceipt(false); setReceiptDialog(false); setSuccess('Receipt settings saved!'); setTimeout(() => setSuccess(''), 2000); }}
            variant="contained" disabled={savingReceipt}
            startIcon={savingReceipt ? <CircularProgress size={14} color="inherit"/> : null}
            sx={{ background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, borderRadius:2, fontWeight:700 }}>
            {savingReceipt ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

// ═════════════════════════════════════════════════════
//  ADD VIEW — MOBILE OPTIMIZED + LIVE FARMER SEARCH
// ═════════════════════════════════════════════════════
function AddView({ setView }) {
  const { theme, t, user } = useApp();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const centerName = user?.centerName || 'Dairy Center';
  const todayISO = () => new Date().toISOString().split('T')[0];

  const [date,       setDate]       = useState(todayISO());
  const [shift,      setShift]      = useState(1);
  const [milkType,   setMilkType]   = useState(1);
  const [farmerCode, setFarmerCode] = useState('');
  const [farmer,     setFarmer]     = useState(null);
  const [liter,      setLiter]      = useState('');
  const [fat,        setFat]        = useState('');
  const [snf,        setSnf]        = useState('');
  const [rate,       setRate]       = useState('');
  const [rateInfo,   setRateInfo]   = useState('');
  const [amount,     setAmount]     = useState(0);
  const [loadingF,   setLoadingF]   = useState(false);
  const [loadingR,   setLoadingR]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [todayEntries, setTodayEntries] = useState([]); // existing collections for current date

  const farmerRef    = useRef(null);
  const literRef     = useRef(null);
  const fatRef       = useRef(null);
  const snfRef       = useRef(null);
  const milkRef      = useRef(1);
  const isOnline     = useOnlineStatus();

  // Debounce timer refs
  // No debounce needed — search only on Enter/Tab

  useEffect(() => { milkRef.current = milkType; }, [milkType]);

  useEffect(() => {
    const l = parseFloat(liter) || 0;
    const r = parseFloat(rate)  || 0;
    setAmount(Math.round(l * r * 100) / 100);
  }, [liter, rate]);

  useEffect(() => {
    const f = parseFloat(fat);
    const s = parseFloat(snf);
    if (isNaN(f) || isNaN(s) || f <= 0 || s <= 0) { setRate(''); setRateInfo(''); return; }
    if (!isOnline) {
      (async () => {
        const offlineRate = await getRateOffline(milkRef.current, f, s);
        if (offlineRate) { setRate(offlineRate.toFixed(4)); setRateInfo(`📦 Offline rate: ₹${offlineRate.toFixed(2)}`); }
        else setRateInfo('Offline — enter rate manually ↑');
      })();
      return;
    }
    const mt = milkRef.current;
    let cancelled = false;
    setLoadingR(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/collection/rate?milkTypeId=${mt}&fat=${f}&snf=${s}`);
        if (cancelled) return;
        const val = parseFloat(res.data?.data);
        if (res.data?.success && val > 0) { setRate(val.toFixed(4)); setRateInfo(`${mt === 1 ? 'Cow' : 'Buffalo'} Rate: ${val.toFixed(2)}`); }
        else { setRate(''); setRateInfo('No rate found for this FAT/SNF'); }
      } catch { if (!cancelled) setRateInfo('Rate fetch failed'); }
      finally  { if (!cancelled) setLoadingR(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); setLoadingR(false); };
  }, [fat, snf, milkType, isOnline]);

  // ── Debounce ref for live search ──
  const searchTimerRef = useRef(null);

  // ✅ Core search — uses /farmer?search= to find by farmerCode (not farmerId)
  const doSearch = useCallback(async (code) => {
    const trimmed = code.trim();
    if (!trimmed) { setFarmer(null); return; }

    if (!isOnline) {
      const cached = await getFarmerOffline(trimmed);
      if (cached) {
        setFarmer(cached);
        const mt = (!cached.milkTypeId || cached.milkTypeId === 3) ? 1 : cached.milkTypeId;
        setMilkType(mt); milkRef.current = mt;
      } else { setFarmer(null); }
      return;
    }

    setLoadingF(true);
    try {
      // ✅ Search by farmerCode using /farmer?search= API
      // This correctly matches farmerCode="8" → Satish, not farmerId=8 → Ajit
      const res = await api.get('/farmer', { params: { search: trimmed, pageSize: 10 } });
      const list = res.data?.data || [];

      // Find exact farmerCode match first (case-insensitive)
      let match = list.find(f =>
        String(f.farmerCode).trim().toLowerCase() === trimmed.toLowerCase()
      );

      // If no exact match, take first result that starts with the code
      if (!match && list.length > 0) {
        match = list.find(f =>
          String(f.farmerCode).trim().toLowerCase().startsWith(trimmed.toLowerCase())
        );
      }

      if (match) {
        // Map to the shape expected by AddView
        const farmer = {
          farmerId:    match.farmerId,
          farmerCode:  match.farmerCode,
          farmerName:  match.farmerName,
          firstName:   match.firstName,
          lastName:    match.lastName,
          milkTypeId:  match.milkTypeId,
          milkTypeName:match.milkTypeName,
          mobileNumber:match.mobileNumber,
          phoneNumber: match.phoneNumber,
        };
        setFarmer(farmer);
        const mt = (!match.milkTypeId || match.milkTypeId === 3) ? 1 : match.milkTypeId;
        setMilkType(mt); milkRef.current = mt;
      } else {
        setFarmer(null);
      }
    } catch { setFarmer(null); }
    finally { setLoadingF(false); }
  }, [isOnline]);

  // ✅ LIVE SEARCH on every keypress — with 300ms debounce
  // Cursor STAYS in farmer field — only Tab/Enter moves it
  const handleFarmerCodeChange = (e) => {
    const val = e.target.value.toUpperCase();
    setFarmerCode(val);
    setFarmer(null);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val.trim()) return;

    // Fire search 300ms after user stops typing
    searchTimerRef.current = setTimeout(() => {
      doSearch(val.trim());
    }, 300);
  };

  // ✅ Enter = search immediately + stay in field. Tab = move naturally.
  const handleFarmerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      doSearch(farmerCode.trim());
    }
    // Tab: browser moves focus naturally — no intercept needed
  };

  // No auto-focus — cursor stays where user puts it

  const handleReset = useCallback(() => {
    setFarmerCode(''); setFarmer(null); setLiter(''); setFat('');
    setSnf(''); setRate(''); setRateInfo(''); setAmount(0);
    setError(''); setSuccess(''); setShift(1); setMilkType(1);
    setTodayEntries([]);
    milkRef.current = 1;
    setTimeout(() => farmerRef.current?.focus(), 50);
  }, []);

  const handleSave = async () => {
    setError('');
    if (!farmer) { setError('Select a farmer first.'); return; }
    if (!liter)  { setError('Enter quantity (Liter).'); return; }
    if (!fat)    { setError('Enter FAT value.'); return; }
    if (!snf)    { setError('Enter SNF value.'); return; }

    // ── Duplicate check ──────────────────────────────────────
    // Rules:
    //   Cow (1) or Buffalo (2)   → max 1 entry per farmer per shift per day
    //   Cow+Buffalo (3)          → max 1 Cow + 1 Buffalo per farmer per shift per day
    try {
      const checkRes = await api.get('/collection', {
        params: { date, pageSize: 999 }
      });
      const existing = checkRes.data?.data?.data || [];

      // Filter entries for this farmer + date + shift
      const sameSlot = existing.filter(c =>
        c.farmerId === farmer.farmerId &&
        String(c.shift) === String(shift)
      );

      if (sameSlot.length > 0) {
        // For Cow (1) or Buffalo (2): only 1 entry allowed per shift
        if (milkType === 1 || milkType === 2) {
          const exactDup = sameSlot.find(c => c.milkTypeId === milkType);
          if (exactDup) {
            const milkLabel = milkType === 1 ? 'Cow' : 'Buffalo';
            const shiftLabel = shift === 1 ? 'Morning' : 'Evening';
            setError(`⚠️ ${milkLabel} collection already saved for ${farmer.farmerName} in ${shiftLabel} shift.`);
            return;
          }
        }
        // For Cow+Buffalo (3): 1 Cow (milkType=1) + 1 Buffalo (milkType=2) allowed per shift
        if (milkType === 3) {
          // milkType 3 means we save as Cow (1) then Buffalo (2) — or the UI has separate entries
          // Block if both Cow and Buffalo already exist for this slot
          const hasCow    = sameSlot.some(c => c.milkTypeId === 1 || c.milkTypeId === 3);
          const hasBuf    = sameSlot.some(c => c.milkTypeId === 2 || c.milkTypeId === 3);
          if (hasCow && hasBuf) {
            const shiftLabel = shift === 1 ? 'Morning' : 'Evening';
            setError(`⚠️ Both Cow and Buffalo collections already saved for ${farmer.farmerName} in ${shiftLabel} shift.`);
            return;
          }
        }
      }
    } catch {
      // If duplicate check fails (offline), proceed and let API handle it
    }
    // ────────────────────────────────────────────────────────

    const payload = {
      farmerId: farmer.farmerId, collectionDate: date,
      shift, milkTypeId: milkType,
      quantity: parseFloat(liter), fat: parseFloat(fat),
      snf: parseFloat(snf), clr: 0,
      rate: parseFloat(rate) || 0, amount,
    };
    setSaving(true);
    try {
      if (!isOnline) {
        await queueOffline(payload);
        setSuccess(t('savedOffline')); handleReset();
        setTimeout(() => setSuccess(''), 3000); return;
      }
      const res = await api.post('/collection', payload);
      if (res.data.success) {
        const rSettings = await getReceiptSettings();
        // ✅ Print silently to default printer, then focus back on farmer code
        printReceipt(
          { ...payload, code: farmer.farmerCode, name: farmer.farmerName,
            collectionDate: payload.collectionDate },
          centerName,
          rSettings.address || '',
          () => { setTimeout(() => farmerRef.current?.focus(), 100); }
        );
        setSuccess(t('collectionSaved'));
        // ✅ Stay on Add Collection page — reset form and focus farmer code
        handleReset();
        setTimeout(() => setSuccess(''), 2500);
      } else { setError(res.data.message || 'Save failed.'); }
    } catch {
      await queueOffline(payload);
      setSuccess('Network error — saved offline!'); handleReset();
      setTimeout(() => setSuccess(''), 3000);
    } finally { setSaving(false); }
  };

  const shiftBtn = (id, label, icon) => {
    const sel = shift === id; const am = id === 1;
    return (
      <Button key={id} size="small" startIcon={icon} onClick={() => { if (!sel) setShift(id); }}
        sx={{ borderRadius:2, fontWeight:700, fontSize:'0.82rem', px:1.8, flex: isMobile ? 1 : 'none', minWidth: isMobile ? 0 : 105,
          border:`2px solid ${am ? '#f59e0b' : '#6366f1'}`,
          color:      sel ? '#fff !important' : (am ? '#d97706' : '#4f46e5'),
          background: sel ? `${am ? '#f59e0b' : '#4f46e5'} !important` : 'white',
          boxShadow:  sel ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
          '&:hover':  { background: sel ? `${am ? '#e08900' : '#3730a3'} !important` : (am ? '#fffbeb' : '#eef2ff') },
        }}>{label}</Button>
    );
  };

  const milkBtn = (id, label) => {
    const sel = milkType === id;
    return (
      <Button key={id} size="small" onClick={() => { if (!sel) setMilkType(id); }}
        sx={{ borderRadius:2, fontWeight:700, fontSize:'0.82rem', px:1.8, flex: isMobile ? 1 : 'none', minWidth: isMobile ? 0 : 115,
          border:'2px solid #1a7a4a',
          color:      sel ? '#fff !important' : theme.primary,
          background: sel ? '#1a7a4a !important' : 'white',
          boxShadow:  sel ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
          '&:hover':  { background: sel ? '#145e38 !important' : theme.light },
        }}>{label}</Button>
    );
  };

  const inpSx = {
    '& input': { fontSize:'0.9rem', py: isMobile ? 1.2 : 0.9, textAlign:'center' },
    '& .MuiOutlinedInput-root': { borderRadius:1 }
  };

  // ── Label chip component ──
  const FieldLabel = ({ text, required }) => (
    <Typography variant="caption" sx={{ fontWeight:700, color:'#555', fontSize:'0.75rem', mb:0.5, display:'block' }}>
      {text}{required && <span style={{ color:'#e53e3e' }}> *</span>}
    </Typography>
  );

  return (
    <Layout title={t('addCollection')}>
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {!isOnline && (
        <Alert severity="warning" icon={<WifiOff/>} sx={{ mb:2, borderRadius:2 }}>
          <strong>Offline mode</strong> — saved locally, syncs when reconnected.
        </Alert>
      )}
      <Card sx={{ borderRadius:3, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', gap:1, borderBottom:'1px solid #f0f0f0' }}>
          <Tooltip title="Back">
            <IconButton size="small" onClick={() => setView('list')} sx={{ color:theme.primary }}>
              <ArrowBack fontSize="small"/>
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ fontWeight:800 }}>{t('addCollection')}</Typography>
        </Box>

        <Box sx={{ px: isMobile ? 2 : 3, py:2.5 }}>
          {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* ── Date + Shift + Milk row ── */}
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:2.5, flexWrap:'wrap' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:'#555', whiteSpace:'nowrap' }}>Date:</Typography>
              <TextField value={date} onChange={(e) => setDate(e.target.value)} type="date" size="small"
                inputProps={{ max: todayISO() }}
                sx={{ width:150, '& input':{ fontSize:'0.83rem', py:0.75 } }}/>
            </Box>
            <Box sx={{ display:'flex', gap:0.8, flex: isMobile ? 1 : 'none' }}>
              {shiftBtn(1, t('morning'), <WbSunny sx={{ fontSize:'15px !important' }}/>)}
              {shiftBtn(2, t('evening'), <NightsStay sx={{ fontSize:'15px !important' }}/>)}
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', gap:0.8, flex: isMobile ? 1 : 'none' }}>
              {!isMobile && <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Milk:</Typography>}
              {milkBtn(1, t('cow'))}
              {milkBtn(2, t('buffalo'))}
            </Box>
          </Box>

          {/* ══════════════════════════════════════════
              MOBILE LAYOUT — vertical card style
          ══════════════════════════════════════════ */}
          {isMobile ? (
            <Box sx={{ border:'1.5px solid #e0e0e0', borderRadius:2, overflow:'hidden', mb:2 }}>

              {/* Farmer Code — full width */}
              <Box sx={{ px:2, py:1.5, borderBottom:'1px solid #f0f0f0', background:'#fafffe' }}>
                <FieldLabel text="Farmer Code" required />
                <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
                  <TextField
                    inputRef={farmerRef}
                    value={farmerCode}
                    onChange={handleFarmerCodeChange}
                    onKeyDown={handleFarmerKeyDown}
                    placeholder="Enter code..."
                    size="small" fullWidth
                    inputProps={{ autoCapitalize:'characters', inputMode:'numeric' }}
                    sx={{ '& input':{ fontSize:'1rem', py:1, letterSpacing:2, fontWeight:700, color: theme.primary } }}
                  />
                  {loadingF && <CircularProgress size={20} sx={{ color:theme.primary, flexShrink:0 }}/>}
                </Box>
                {/* Farmer name display */}
                {farmer ? (
                  <Box sx={{ mt:0.8, px:1.5, py:0.8, background:'#ECFDF5', borderRadius:1.5, border:'1px solid #A7F3D0' }}>
                    <Typography variant="body2" sx={{ color:theme.primary, fontWeight:700, fontSize:'0.9rem' }}>
                      ✅ {farmer.farmerName}
                    </Typography>
                    {farmer.milkTypeName && (
                      <Typography variant="caption" sx={{ color:'#6B7280' }}>{farmer.milkTypeName}</Typography>
                    )}
                    {/* Today's saved entries for this farmer */}
                    {todayEntries.length > 0 && (
                      <Box sx={{ mt:0.5, display:'flex', flexWrap:'wrap', gap:0.5 }}>
                        {todayEntries.map((c,i) => {
                          const isCurrentShift = String(c.shift) === String(shift);
                          const milkLabel = c.milkTypeId===1 ? '🐄 Cow' : c.milkTypeId===2 ? '🐃 Buffalo' : '🐄🐃';
                          const shiftLabel = c.shift===1 ? 'M' : 'E';
                          return (
                            <Box key={i} sx={{ px:0.8, py:0.1, borderRadius:1, fontSize:'0.68rem', fontWeight:700,
                              background: isCurrentShift ? '#FEF9C3' : '#F3F4F6',
                              border: isCurrentShift ? '1px solid #F59E0B' : '1px solid #E5E7EB',
                              color: isCurrentShift ? '#D97706' : '#9CA3AF' }}>
                              {shiftLabel}: {milkLabel}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ) : farmerCode ? (
                  <Typography variant="caption" sx={{ color:'#9CA3AF', mt:0.5, display:'block' }}>
                    {loadingF ? 'Searching...' : 'Farmer not found'}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color:'#9CA3AF', mt:0.5, display:'block' }}>
                    Type → name appears live. Tab or Enter to confirm & move
                  </Typography>
                )}
              </Box>

              {/* Qty + FAT in a row */}
              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', borderBottom:'1px solid #f0f0f0' }}>
                <Box sx={{ px:2, py:1.5, borderRight:'1px solid #f0f0f0' }}>
                  <FieldLabel text="Qty (Liter)" required />
                  <TextField inputRef={literRef} value={liter}
                    onChange={(e) => setLiter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fatRef.current?.focus(); }}
                    type="number" size="small" fullWidth
                    inputProps={{ step:'0.001', min:0, inputMode:'decimal' }} sx={inpSx}/>
                </Box>
                <Box sx={{ px:2, py:1.5 }}>
                  <FieldLabel text="FAT" required />
                  <TextField
                    inputRef={fatRef} value={fat}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9.]/g, '');
                      if ((v.match(/[.]/g) || []).length > 1) return;
                      if (!v.includes('.')) {
                        if (v.length === 2 && v[0] !== '1') v = v[0] + '.' + v[1];
                        else if (v.length === 3 && v[0] === '1') v = v.slice(0,2) + '.' + v[2];
                        else if (v.length >= 3 && v[0] !== '1') return;
                      }
                      const parts = v.split('.');
                      const maxBefore = parts[0][0] === '1' ? 2 : 1;
                      if (parts[0].length > maxBefore) return;
                      setFat(v);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { snfRef.current?.focus(); return; }
                      const ok = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab'];
                      if (!/^[0-9.]$/.test(e.key) && !ok.includes(e.key)) e.preventDefault();
                    }}
                    placeholder='0.0' size='small' fullWidth
                    inputProps={{ inputMode: 'decimal' }} sx={inpSx}/>
                </Box>
              </Box>

              {/* SNF + Rate in a row */}
              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', borderBottom:'1px solid #f0f0f0' }}>
                <Box sx={{ px:2, py:1.5, borderRight:'1px solid #f0f0f0' }}>
                  <FieldLabel text="SNF" required />
                  <TextField
                    inputRef={snfRef} value={snf}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9.]/g, '');
                      if ((v.match(/[.]/g) || []).length > 1) return;
                      if (!v.includes('.')) {
                        if (v.length === 2 && v[0] !== '1') v = v[0] + '.' + v[1];
                        else if (v.length === 3 && v[0] === '1') v = v.slice(0,2) + '.' + v[2];
                        else if (v.length >= 3 && v[0] !== '1') return;
                      }
                      const parts = v.split('.');
                      const maxBefore = parts[0][0] === '1' ? 2 : 1;
                      if (parts[0].length > maxBefore) return;
                      setSnf(v);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { handleSave(); return; }
                      const ok = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab'];
                      if (!/^[0-9.]$/.test(e.key) && !ok.includes(e.key)) e.preventDefault();
                    }}
                    placeholder='0.0' size='small' fullWidth
                    inputProps={{ inputMode: 'decimal' }} sx={inpSx}/>
                </Box>
                <Box sx={{ px:2, py:1.5, position:'relative' }}>
                  <FieldLabel text="Rate (auto)" />
                  <TextField value={rate} onChange={(e) => setRate(e.target.value)} type="number" size="small" fullWidth
                    placeholder={!isOnline ? 'Enter manually' : ''}
                    inputProps={{ step:'0.01', min:0, inputMode:'decimal' }}
                    sx={{ ...inpSx, '& .MuiOutlinedInput-root':{ background: rate ? theme.light : !isOnline ? '#fff8f3' : 'transparent' } }}/>
                  {loadingR && <CircularProgress size={12} sx={{ position:'absolute', right:16, top:'50%', mt:'-6px', color:theme.primary }}/>}
                </Box>
              </Box>

              {/* Rate info + Amount */}
              <Box sx={{ px:2, py:1.5, background:'#FAFFFE' }}>
                {rateInfo && (
                  <Typography variant="caption" sx={{ color:rateInfo.startsWith('No')||rateInfo.startsWith('Off') ? '#f97316' : theme.primary, fontWeight:600, display:'block', mb:0.5 }}>
                    📊 {rateInfo}
                  </Typography>
                )}
                {amount > 0 && (
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mt:0.5 }}>
                    <Typography variant="caption" sx={{ color:'#6B7280', fontWeight:600 }}>Total Amount</Typography>
                    <Typography variant="h5" sx={{ fontWeight:900, color:theme.primary }}>₹ {fmt2(amount)}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            /* ══════════════════════════════════════════
                DESKTOP LAYOUT — horizontal table style
            ══════════════════════════════════════════ */
            <Box sx={{ border:'1.5px solid #e0e0e0', borderRadius:2, overflow:'hidden', mb:2 }}>
              <Box sx={{ display:'grid', gridTemplateColumns:'260px 1fr 1fr 1fr 1fr', background:'#f8fffe', borderBottom:'1px solid #e0e0e0' }}>
                {[t('farmerCode'),'Liter *','FAT *','SNF *',t('rate')].map((h, i) => (
                  <Box key={h} sx={{ px:2, py:1.2, borderLeft:i===0?'none':'1px solid #e0e0e0', fontWeight:700, fontSize:'0.8rem', color:'#555' }}>
                    {h.includes('*') ? <>{h.replace(' *','')} <span style={{ color:'#e53e3e' }}>*</span></> : h}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display:'grid', gridTemplateColumns:'260px 1fr 1fr 1fr 1fr' }}>
                <Box sx={{ px:1.5, py:1.2, display:'flex', alignItems:'center', gap:0.5, borderRight:'1px solid #e0e0e0' }}>
                  <TextField
                    inputRef={farmerRef}
                    value={farmerCode}
                    onChange={handleFarmerCodeChange}
                    onKeyDown={handleFarmerKeyDown}
                    placeholder="Enter code..."
                    size="small" fullWidth
                    inputProps={{ autoCapitalize:'characters' }}
                    sx={{ '& input':{ fontSize:'0.82rem', py:0.7 } }}
                  />
                  {loadingF
                    ? <CircularProgress size={14} sx={{ flexShrink:0 }}/>
                    : <IconButton size="small" onClick={() => doSearch(farmerCode.trim())}
                        sx={{ color:theme.primary, border:'1px solid #d1fae5', borderRadius:1.5, p:0.5, flexShrink:0 }}>
                        <Search fontSize="small"/>
                      </IconButton>
                  }
                </Box>

                <Box sx={{ px:1.5, py:1.2, borderRight:'1px solid #e0e0e0' }}>
                  <TextField inputRef={literRef} value={liter} onChange={(e) => setLiter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fatRef.current?.focus(); }}
                    type="number" size="small" fullWidth
                    inputProps={{ step:'0.001', min:0, inputMode:'decimal' }} sx={inpSx}/>
                </Box>

                <Box sx={{ px:1.5, py:1.2, borderRight:'1px solid #e0e0e0' }}>
                  <TextField inputRef={fatRef} value={fat}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9.]/g, '');
                      if ((v.match(/[.]/g) || []).length > 1) return;
                      if (!v.includes('.')) {
                        if (v.length === 2 && v[0] !== '1') v = v[0] + '.' + v[1];
                        else if (v.length === 3 && v[0] === '1') v = v.slice(0,2) + '.' + v[2];
                        else if (v.length >= 3 && v[0] !== '1') return;
                      }
                      const parts = v.split('.');
                      const maxBefore = parts[0][0] === '1' ? 2 : 1;
                      if (parts[0].length > maxBefore) return;
                      setFat(v);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { snfRef.current?.focus(); return; }
                      const ok = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab'];
                      if (!/^[0-9.]$/.test(e.key) && !ok.includes(e.key)) e.preventDefault();
                    }}
                    placeholder='0.0' size='small' fullWidth
                    inputProps={{ inputMode: 'decimal' }} sx={inpSx}/>
                </Box>

                <Box sx={{ px:1.5, py:1.2, borderRight:'1px solid #e0e0e0' }}>
                  <TextField inputRef={snfRef} value={snf}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9.]/g, '');
                      if ((v.match(/[.]/g) || []).length > 1) return;
                      if (!v.includes('.')) {
                        if (v.length === 2 && v[0] !== '1') v = v[0] + '.' + v[1];
                        else if (v.length === 3 && v[0] === '1') v = v.slice(0,2) + '.' + v[2];
                        else if (v.length >= 3 && v[0] !== '1') return;
                      }
                      const parts = v.split('.');
                      const maxBefore = parts[0][0] === '1' ? 2 : 1;
                      if (parts[0].length > maxBefore) return;
                      setSnf(v);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { handleSave(); return; }
                      const ok = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab'];
                      if (!/^[0-9.]$/.test(e.key) && !ok.includes(e.key)) e.preventDefault();
                    }}
                    placeholder='0.0' size='small' fullWidth
                    inputProps={{ inputMode: 'decimal' }} sx={inpSx}/>
                </Box>

                <Box sx={{ px:1.5, py:1.2, position:'relative' }}>
                  <TextField value={rate} onChange={(e) => setRate(e.target.value)} type="number" size="small" fullWidth
                    placeholder={!isOnline ? 'Enter manually' : ''}
                    inputProps={{ step:'0.01', min:0, inputMode:'decimal' }}
                    sx={{ ...inpSx, '& .MuiOutlinedInput-root':{ borderRadius:1, background: rate ? theme.light : !isOnline ? '#fff8f3' : 'transparent' } }}/>
                  {loadingR && <CircularProgress size={12} sx={{ position:'absolute', right:16, top:'50%', mt:'-6px', color:theme.primary }}/>}
                </Box>
              </Box>

              <Box sx={{ px:2, py:1, display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f8fffe', flexWrap:'wrap', gap:1, borderTop:'1px solid #f0f0f0' }}>
                <Box>
                  {farmer ? (
                    <Box>
                      <Typography variant="body2" sx={{ color:theme.primary, fontWeight:700 }}>
                        ✅ {farmer.farmerName}
                        {farmer.milkTypeName && <span style={{ color:'#888', fontWeight:400, marginLeft:8 }}>({farmer.milkTypeName})</span>}
                      </Typography>
                      {todayEntries.length > 0 && (
                        <Box sx={{ display:'flex', gap:0.5, mt:0.3, flexWrap:'wrap' }}>
                          {todayEntries.map((c,i) => {
                            const isCurShift = String(c.shift) === String(shift);
                            const milkLabel = c.milkTypeId===1 ? '🐄 Cow' : c.milkTypeId===2 ? '🐃 Buffalo' : '🐄🐃';
                            const sLabel = c.shift===1 ? 'M' : 'E';
                            return (
                              <Box key={i} sx={{ px:1, py:0.2, borderRadius:1.5, fontSize:'0.7rem', fontWeight:700,
                                background: isCurShift ? '#FEF9C3' : '#F3F4F6',
                                border: isCurShift ? '1px solid #F59E0B' : '1px solid #E5E7EB',
                                color: isCurShift ? '#D97706' : '#9CA3AF' }}>
                                {milkLabel} {sLabel} ✓
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color:'#bbb' }}>
                      {loadingF ? 'Searching...' : 'Type code → name loads live. Tab to move next field'}
                    </Typography>
                  )}
                  {rateInfo && (
                    <Typography variant="caption" sx={{
                      color: rateInfo.startsWith('No') || rateInfo.startsWith('Off') ? '#f97316' : theme.primary,
                      display:'block', mt:0.3, fontWeight:600
                    }}>📊 {rateInfo}</Typography>
                  )}
                </Box>
                {amount > 0 && (
                  <Box sx={{ px:2, py:0.5, background:theme.light, borderRadius:2, border:'1px solid #a5d6a7' }}>
                    <Typography variant="body2" sx={{ fontWeight:800, color:theme.primary, fontSize:'1rem' }}>₹ {fmt2(amount)}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ display:'flex', justifyContent:'flex-end', gap:2, mt:2 }}>
            <Button onClick={handleReset} variant="outlined" sx={{ borderRadius:2, color:'#888', borderColor:'#ddd', flex: isMobile ? 1 : 'none' }}>{t('reset')}</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit"/> : !isOnline ? <CloudOff sx={{ fontSize:16 }}/> : null}
              sx={{
                borderRadius:2, fontWeight:700, flex: isMobile ? 2 : 'none',
                background: !isOnline ? 'linear-gradient(135deg,#f97316,#ea6c00)' : `linear-gradient(135deg,${theme.primary},${theme.hover})`,
                '&:hover': { background: !isOnline ? '#c45100' : `linear-gradient(135deg,${theme.hover},${theme.primary})` }
              }}>
              {saving ? t('saving') : !isOnline ? t('saveOffline') : t('save')}
            </Button>
          </Box>
        </Box>
      </Card>
    </Layout>
  );
}

// ═════════════════════════════════════════════════════
//  OFFLINE QUEUE VIEW
// ═════════════════════════════════════════════════════
function OfflineQueueView({ setView }) {
  const { theme, t } = useApp();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const isOnline = useOnlineStatus();

  const loadEntries = async () => {
    setLoading(true);
    try { const all = await getAllQueue(); setEntries(all.reverse()); }
    catch { setError('Failed to load offline queue.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadEntries(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { synced, failed } = await syncQueue();
      await loadEntries();
      setSuccess(`Sync done: ${synced} saved${failed > 0 ? `, ${failed} failed` : ''}. ✅`);
      setTimeout(() => setSuccess(''), 4000);
    } catch { setError('Sync failed.'); }
    finally { setSyncing(false); }
  };

  const pendingCount = entries.filter(e => e.status === 'pending').length;
  const thSx = { background:'#f8fffe', fontWeight:800, color:'#444', fontSize:'0.75rem', padding:'10px 12px', borderBottom:'2px solid #e0f2e9', whiteSpace:'nowrap', textAlign:'left' };
  const tdSx = { fontSize:'0.78rem', padding:'8px 12px', borderBottom:'1px solid #f5f5f5', whiteSpace:'nowrap' };

  return (
    <Layout title="Offline Queue">
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb:2, borderRadius:2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ borderRadius:3, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #f0f0f0', flexWrap:'wrap', gap:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Tooltip title="Back"><IconButton size="small" onClick={() => setView('list')} sx={{ color:theme.primary }}><ArrowBack fontSize="small"/></IconButton></Tooltip>
            <CloudOff sx={{ color:'#f97316', fontSize:22 }}/>
            <Typography variant="h6" sx={{ fontWeight:800 }}>{t('offlineQueue')}</Typography>
            {pendingCount > 0 && <Chip label={`${pendingCount} pending`} size="small" sx={{ background:'#fff7ed', color:'#f97316', fontWeight:700, fontSize:'0.7rem', border:'1px solid #f9731633' }}/>}
          </Box>
          <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:0.5, px:1.5, py:0.5, background:isOnline?theme.light:'#fff7ed', borderRadius:2, border:`1px solid ${isOnline?'#1a7a4a33':'#f9731633'}` }}>
              {isOnline ? <CloudDone sx={{ fontSize:16, color:theme.primary }}/> : <WifiOff sx={{ fontSize:16, color:'#f97316' }}/>}
              <Typography variant="caption" sx={{ fontWeight:700, color:isOnline?theme.primary:'#f97316' }}>{isOnline ? 'Online' : 'Offline'}</Typography>
            </Box>
            {isOnline && pendingCount > 0 && (
              <Button variant="contained" size="small" disabled={syncing}
                startIcon={syncing ? <CircularProgress size={14} color="inherit"/> : <Sync sx={{ fontSize:16 }}/>}
                onClick={handleSync}
                sx={{ background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, borderRadius:2, fontWeight:700 }}>
                {syncing ? t('syncing') : `Sync ${pendingCount} Entries`}
              </Button>
            )}
            <Tooltip title="Refresh"><IconButton size="small" onClick={loadEntries} sx={{ border:'1px solid #e0e0e0', borderRadius:1.5, color:theme.primary }}><Refresh fontSize="small"/></IconButton></Tooltip>
          </Box>
        </Box>

        {!isOnline && (
          <Box sx={{ px:2.5, py:1, background:'#fff7ed', borderBottom:'1px solid #fed7aa' }}>
            <Typography variant="caption" sx={{ color:'#f97316', fontWeight:600 }}>📡 Connect to the internet to sync these entries to the server.</Typography>
          </Box>
        )}

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['#',t('date'),t('shift'),'Milk','Farmer ID','Qty (L)','FAT','SNF',t('rate'),t('amount'),t('status'),'Saved At'].map(col => <th key={col} style={thSx}>{col}</th>)}</tr></thead>
            <tbody>
              {loading ? Array.from({ length:3 }).map((_,i) => <tr key={i}>{Array.from({ length:12 }).map((__,j) => <td key={j} style={tdSx}><Skeleton height={18}/></td>)}</tr>)
              : entries.length === 0 ? <tr><td colSpan={12} style={{ textAlign:'center' }}><Typography variant="body2" sx={{ color:'#bbb', py:5 }}>Queue is empty ✅</Typography></td></tr>
              : entries.map((entry, idx) => (
                <tr key={entry.localId} style={{ background:idx%2===0?'#fff':'#fafffe', opacity:entry.status==='synced'?0.6:1 }}>
                  <td style={{ ...tdSx, color:'#aaa', fontSize:'0.7rem' }}>{entry.localId}</td>
                  <td style={tdSx}>{entry.collectionDate}</td>
                  <td style={tdSx}><ShiftBadge shift={entry.shift}/></td>
                  <td style={tdSx}><MilkBadge typeId={entry.milkTypeId}/></td>
                  <td style={{ ...tdSx, fontWeight:700, color:theme.primary }}>{entry.farmerId}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt3(entry.quantity)}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt2(entry.fat)}</td>
                  <td style={{ ...tdSx, textAlign:'right' }}>{fmt2(entry.snf)}</td>
                  <td style={{ ...tdSx, textAlign:'right', color:theme.primary, fontWeight:700 }}>{fmt2(entry.rate)}</td>
                  <td style={{ ...tdSx, textAlign:'right', fontWeight:700 }}>₹{fmt2(entry.amount)}</td>
                  <td style={tdSx}><StatusChip status={entry.status}/></td>
                  <td style={{ ...tdSx, color:'#999', fontSize:'0.72rem' }}>{entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-IN') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {entries.length > 0 && (
          <Box sx={{ px:2.5, py:1.2, borderTop:'1px solid #f0f0f0', background:'#fafafa', display:'flex', gap:3 }}>
            <Typography variant="caption" sx={{ color:'#f97316', fontWeight:700 }}>Pending: {entries.filter(e=>e.status==='pending').length}</Typography>
            <Typography variant="caption" sx={{ color:theme.primary, fontWeight:700 }}>Synced: {entries.filter(e=>e.status==='synced').length}</Typography>
            <Typography variant="caption" sx={{ color:'#e53e3e', fontWeight:700 }}>Failed: {entries.filter(e=>e.status==='failed').length}</Typography>
          </Box>
        )}
      </Card>
    </Layout>
  );
}

// ═════════════════════════════════════════════════════
//  ADJUSTMENT VIEW
// ═════════════════════════════════════════════════════
function AdjustmentView({ setView }) {
  const { theme, t } = useApp();
  const [filterDate,  setFilterDate]  = useState(new Date().toISOString().split('T')[0]);
  const [farmerCode,  setFarmerCode]  = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [filterMilk,  setFilterMilk]  = useState('');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [data,        setData]        = useState([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [editRow,     setEditRow]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const btnSx = { background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, borderRadius:2, fontWeight:700, fontSize:'0.8rem', '&:hover':{ background:`linear-gradient(135deg,${theme.hover},${theme.primary})` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filterDate)  params.append('date', filterDate);
      if (farmerCode)  params.append('farmerCode', farmerCode);
      if (filterShift) params.append('shift',      filterShift);
      if (filterMilk)  params.append('milkTypeId', filterMilk);
      const res = await api.get(`/collection/adjustment?${params}`);
      if (res.data.success) { setData(res.data.data?.data || []); setTotalCount(res.data.data?.totalCount || 0); }
    } catch {}
    finally { setLoading(false); }
  }, [page, pageSize, filterDate, farmerCode, filterShift, filterMilk]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const res = await api.put('/collection', {
        collectionId: editRow.collectionId,
        quantity: parseFloat(editRow.quantity), fat: parseFloat(editRow.fat),
        snf: parseFloat(editRow.snf), clr: parseFloat(editRow.clr || 0),
        rate: parseFloat(editRow.rate), amount: parseFloat(editRow.amount),
      });
      if (res.data.success) { setSuccess('Collection updated!'); setEditRow(null); fetchData(); setTimeout(() => setSuccess(''), 2500); }
      else setError(res.data.message);
    } catch { setError('Update failed.'); }
    finally { setSaving(false); }
  };

  const handleEditChange = (field, value) => {
    setEditRow(prev => {
      const u = { ...prev, [field]: value };
      if (field === 'quantity' || field === 'rate') u.amount = fmt2(parseFloat(u.quantity||0) * parseFloat(u.rate||0));
      return u;
    });
  };

  const thSx = { background:'#f8fffe', fontWeight:800, color:'#444', fontSize:'0.75rem', padding:'10px 12px', borderBottom:'2px solid #e0f2e9', whiteSpace:'nowrap', textAlign:'left' };
  const tdSx = { fontSize:'0.78rem', padding:'8px 12px', borderBottom:'1px solid #f5f5f5', whiteSpace:'nowrap' };
  const editInp = (field, step='0.01') => (
    <TextField value={editRow?.[field] ?? ''} onChange={(e) => handleEditChange(field, e.target.value)} type="number" size="small"
      inputProps={{ step }} sx={{ width:80, '& input':{ fontSize:'0.75rem', py:0.5, textAlign:'center' } }}/>
  );

  return (
    <Layout title="Collection Adjustment">
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb:2, borderRadius:2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ borderRadius:3, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', gap:1, borderBottom:'1px solid #f0f0f0' }}>
          <Tooltip title="Back"><IconButton size="small" onClick={() => setView('list')} sx={{ color:theme.primary }}><ArrowBack fontSize="small"/></IconButton></Tooltip>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Collection Adjustment</Typography>
        </Box>

        <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', gap:2, flexWrap:'wrap', borderBottom:'1px solid #f5f5f5', background:'#fafffe' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Date :</Typography>
            <TextField value={filterDate} onChange={(e) => setFilterDate(e.target.value)} type="date" size="small" sx={{ width:155, '& input':{ fontSize:'0.82rem', py:0.8 } }}/>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Farmer :</Typography>
            <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
              <TextField value={farmerCode} onChange={(e) => setFarmerCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key==='Enter' && fetchData()} placeholder="Farmer Code" size="small"
                sx={{ width:140, '& input':{ fontSize:'0.82rem', py:0.8 } }}/>
              <IconButton size="small" onClick={fetchData} sx={{ color:theme.primary, border:'1px solid #d1fae5', borderRadius:1.5, p:0.5 }}><Search fontSize="small"/></IconButton>
            </Box>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Shift :</Typography>
            <Select value={filterShift} onChange={(e) => setFilterShift(e.target.value)} size="small" displayEmpty sx={{ minWidth:120, fontSize:'0.82rem', '& .MuiSelect-select':{ py:0.8 } }}>
              <MenuItem value="">All</MenuItem><MenuItem value="1">{t('morning')}</MenuItem><MenuItem value="2">{t('evening')}</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:'#555' }}>Milk :</Typography>
            <Select value={filterMilk} onChange={(e) => setFilterMilk(e.target.value)} size="small" displayEmpty sx={{ minWidth:120, fontSize:'0.82rem', '& .MuiSelect-select':{ py:0.8 } }}>
              <MenuItem value="">All</MenuItem><MenuItem value="1">{t('cow')}</MenuItem><MenuItem value="2">{t('buffalo')}</MenuItem>
            </Select>
          </Box>
          <Button variant="contained" size="small" onClick={() => { setPage(1); fetchData(); }} sx={btnSx}>{t('search')}</Button>
        </Box>

        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{[t('code'),t('name'),t('shift'),'Milk','Quantity','FAT','SNF','CLR',t('rate'),t('amount'),t('action')].map(col => (
              <th key={col} style={{ ...thSx, textAlign:['Quantity','FAT','SNF','CLR',t('rate'),t('amount')].includes(col)?'right':'left' }}>{col}</th>
            ))}</tr></thead>
            <tbody>
              {loading ? Array.from({ length:5 }).map((_,i) => <tr key={i}>{Array.from({ length:11 }).map((__,j) => <td key={j} style={tdSx}><Skeleton height={18}/></td>)}</tr>)
              : data.length === 0 ? <tr><td colSpan={11} style={{ textAlign:'center' }}><Typography variant="body1" sx={{ color:'#bbb', py:4 }}>{t('noRecords')}</Typography></td></tr>
              : data.map((row, idx) => {
                const isEditing = editRow?.collectionId === row.collectionId;
                return (
                  <tr key={row.collectionId} style={{ background:idx%2===0?'#fff':'#fafffe' }}>
                    <td style={{ ...tdSx, fontWeight:700, color:theme.primary }}>{row.code}</td>
                    <td style={tdSx}>{row.name}</td>
                    <td style={tdSx}><ShiftBadge shift={row.shift}/></td>
                    <td style={tdSx}><MilkBadge typeId={row.milkTypeId}/></td>
                    <td style={{ ...tdSx, textAlign:'right' }}>{isEditing ? editInp('quantity','0.001') : fmt3(row.quantity)}</td>
                    <td style={{ ...tdSx, textAlign:'right' }}>{isEditing ? editInp('fat','0.1') : fmt2(row.fat)}</td>
                    <td style={{ ...tdSx, textAlign:'right' }}>{isEditing ? editInp('snf','0.1') : fmt2(row.snf)}</td>
                    <td style={{ ...tdSx, textAlign:'right' }}>{isEditing ? editInp('clr','0.1') : fmt2(row.clr)}</td>
                    <td style={{ ...tdSx, textAlign:'right', color:theme.primary, fontWeight:700 }}>{isEditing ? editInp('rate','0.01') : fmt2(row.rate)}</td>
                    <td style={{ ...tdSx, textAlign:'right', fontWeight:700 }}>
                      {isEditing ? <Typography variant="body2" sx={{ fontWeight:800, color:theme.primary }}>₹{editRow?.amount ?? fmt2(row.amount)}</Typography> : `₹${fmt2(row.amount)}`}
                    </td>
                    <td style={tdSx}>
                      {isEditing ? (
                        <Box sx={{ display:'flex', gap:0.5 }}>
                          <Tooltip title="Save"><IconButton size="small" onClick={handleSaveEdit} disabled={saving} sx={{ color:theme.primary, p:0.3 }}>{saving ? <CircularProgress size={14}/> : <span style={{ fontSize:14 }}>✅</span>}</IconButton></Tooltip>
                          <Tooltip title="Cancel"><IconButton size="small" onClick={() => setEditRow(null)} sx={{ color:'#888', p:0.3 }}><Close sx={{ fontSize:14 }}/></IconButton></Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditRow({ ...row })} sx={{ color:theme.primary, p:0.3 }}><Edit sx={{ fontSize:15 }}/></IconButton></Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
        <PaginationBar page={page} pageSize={pageSize} totalCount={totalCount} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }}/>
      </Card>
    </Layout>
  );
}